import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Loader2, ArrowLeft, Building2, Store } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

const Logo = () => (
  <Link to="/" className="flex items-center gap-2.5">
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="1" width="14" height="6" rx="1" fill="#f43f5e" />
      <rect x="16" y="1" width="16" height="6" rx="1" fill="#e11d48" />
      <rect x="0" y="9" width="9" height="6" rx="1" fill="#e11d48" />
      <rect x="11" y="9" width="11" height="6" rx="1" fill="#f43f5e" />
      <rect x="24" y="9" width="8" height="6" rx="1" fill="#e11d48" />
      <rect x="0" y="17" width="15" height="6" rx="1" fill="#f43f5e" />
      <rect x="17" y="17" width="15" height="6" rx="1" fill="#e11d48" />
      <rect x="0" y="25" width="10" height="6" rx="1" fill="#e11d48" />
      <rect x="12" y="25" width="12" height="6" rx="1" fill="#f43f5e" />
      <rect x="26" y="25" width="6" height="6" rx="1" fill="#e11d48" />
    </svg>
    <span className="text-lg font-semibold tracking-tight text-white">
      saleswall
    </span>
  </Link>
);

interface RoleOption {
  id: string;
  label: string;
  icon: LucideIcon;
  description: string;
}

const ROLES: RoleOption[] = [
  { id: 'vendor', label: 'Vendor', icon: Building2, description: 'Submit pitches to merchants' },
  { id: 'merchant', label: 'Merchant', icon: Store, description: 'Receive evaluated pitches' },
];

interface RegisterFormData {
  email: string;
  password: string;
  name: string;
  company_name: string;
  company_domain: string;
}

export default function RegisterPage() {
  const [step, setStep] = useState(1);
  const [role, setRole] = useState('');
  const [formData, setFormData] = useState<RegisterFormData>({
    email: '',
    password: '',
    name: '',
    company_name: '',
    company_domain: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { register } = useAuth();
  const navigate = useNavigate();

  const handleRoleSelect = (selectedRole: string) => {
    setRole(selectedRole);
    setStep(2);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await register({ ...formData, role });
      const dashboardRoutes: Record<string, string> = {
        vendor: '/vendor',
        merchant: '/merchant',
        admin: '/admin',
      };
      navigate(dashboardRoutes[user.role] ?? '/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex flex-col">
      <div className="p-6">
        <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-rose-400 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to home</span>
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 pb-12">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <Logo />
          </div>

          <div className="bg-[#18181b] rounded-2xl border border-white/10 p-8">
            {step === 1 ? (
              <>
                <div className="text-center mb-8">
                  <h1 className="text-2xl font-bold text-white mb-2">Create an account</h1>
                  <p className="text-zinc-400 text-sm">Select your account type to get started</p>
                </div>

                <div className="space-y-3">
                  {ROLES.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => handleRoleSelect(r.id)}
                      data-testid={`register-role-${r.id}`}
                      className="w-full p-4 rounded-xl border-2 border-white/10 hover:border-rose-500/50 hover:bg-rose-500/5 transition-all duration-200 text-left group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-3 rounded-lg bg-white/5 group-hover:bg-rose-500/10 transition-colors">
                          <r.icon className="w-5 h-5 text-zinc-400 group-hover:text-rose-400" />
                        </div>
                        <div>
                          <div className="font-semibold text-white">{r.label}</div>
                          <div className="text-sm text-zinc-500">{r.description}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="mt-6 text-center">
                  <p className="text-sm text-zinc-400">
                    Already have an account?{' '}
                    <Link to="/login" className="text-rose-400 hover:text-rose-300 font-medium">
                      Sign in
                    </Link>
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="text-center mb-8">
                  <button
                    onClick={() => setStep(1)}
                    className="inline-flex items-center gap-1 text-zinc-500 hover:text-zinc-300 text-sm mb-4"
                  >
                    <ArrowLeft className="w-3 h-3" />
                    Change role
                  </button>
                  <h1 className="text-2xl font-bold text-white mb-2">
                    {role === 'vendor' ? 'Vendor' : 'Merchant'} Registration
                  </h1>
                  <p className="text-zinc-400 text-sm">Fill in your details to create your account</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-400 text-sm" data-testid="register-error">
                      {error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-zinc-300">Full Name</Label>
                    <Input
                      id="name"
                      name="name"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="register-name-input"
                      className="h-11 bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus:border-rose-500/50 focus:ring-rose-500/20"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-zinc-300">Work Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@company.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="register-email-input"
                      className="h-11 bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus:border-rose-500/50 focus:ring-rose-500/20"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-zinc-300">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Min. 8 characters"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        minLength={8}
                        data-testid="register-password-input"
                        className="h-11 pr-10 bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus:border-rose-500/50 focus:ring-rose-500/20"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company_name" className="text-zinc-300">Company Name</Label>
                    <Input
                      id="company_name"
                      name="company_name"
                      placeholder="Acme Inc"
                      value={formData.company_name}
                      onChange={handleChange}
                      required
                      data-testid="register-company-input"
                      className="h-11 bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus:border-rose-500/50 focus:ring-rose-500/20"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company_domain" className="text-zinc-300">Company Website (optional)</Label>
                    <Input
                      id="company_domain"
                      name="company_domain"
                      placeholder="acme.com"
                      value={formData.company_domain}
                      onChange={handleChange}
                      data-testid="register-domain-input"
                      className="h-11 bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus:border-rose-500/50 focus:ring-rose-500/20"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="register-submit-btn"
                    className="w-full h-11 bg-rose-500 hover:bg-rose-600 text-white font-medium mt-2"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating account...
                      </>
                    ) : (
                      'Create account'
                    )}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-sm text-zinc-400">
                    Already have an account?{' '}
                    <Link to="/login" className="text-rose-400 hover:text-rose-300 font-medium">
                      Sign in
                    </Link>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
